/**
 * Hook for fetching and managing majority judgment data for models
 */

import { useState, useEffect, useRef } from 'react';
import { collection, doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from './useAuth';
import { MajorityJudgment, compareMajorityJudgments, readMajorityJudgment } from '../services/majorityJudgmentService';
import { formatMajorityGrade } from '../services/majorityJudgment';
import { getGradeDescriptions, type GradeDescriptions } from '../services/gradeDescriptionsService';

export interface ModelMajorityJudgmentData {
  modelId: string;
  userJudgment: MajorityJudgment | null; // User-specific judgment
  globalJudgment: MajorityJudgment | null; // Global judgment
  lastUpdated: Date | null;
}

/**
 * Hook to get majority judgment for a specific model
 */
export function useModelMajorityJudgment(modelId: string | null): ModelMajorityJudgmentData | null {
  const { user } = useAuth();
  const [data, setData] = useState<ModelMajorityJudgmentData | null>(null);

  useEffect(() => {
    if (!modelId) {
      setData(null);
      return;
    }

    const docId = modelId.replace(/\//g, '_');

    // Subscribe to user-specific judgment
    let unsubscribeUser: (() => void) | undefined;
    if (user) {
      const userDocRef = doc(db, 'users', user.uid, 'modelGrades', docId);
      unsubscribeUser = onSnapshot(userDocRef, (snapshot) => {
        if (snapshot.exists()) {
          const userData = snapshot.data();
          setData(prev => ({
            modelId,
            userJudgment: readMajorityJudgment(userData),
            globalJudgment: prev?.globalJudgment || null,
            lastUpdated: userData.lastUpdated?.toDate() || null,
          }));
        } else {
          setData(prev => ({
            modelId,
            userJudgment: null,
            globalJudgment: prev?.globalJudgment || null,
            lastUpdated: null,
          }));
        }
      });
    }

    // Subscribe to global judgment
    const globalDocRef = doc(db, 'globalModelGrades', docId);
    const unsubscribeGlobal = onSnapshot(globalDocRef, (snapshot) => {
      if (snapshot.exists()) {
        const globalData = snapshot.data();
        setData(prev => ({
          modelId,
          userJudgment: prev?.userJudgment || null,
          globalJudgment: readMajorityJudgment(globalData),
          lastUpdated: globalData.lastUpdated?.toDate() || null,
        }));
      } else {
        setData(prev => ({
          modelId,
          userJudgment: prev?.userJudgment || null,
          globalJudgment: null,
          lastUpdated: null,
        }));
      }
    });

    return () => {
      unsubscribeUser?.();
      unsubscribeGlobal();
    };
  }, [modelId, user]);

  return data;
}

/**
 * Get all models with majority judgments (for sidebar leaderboard)
 * Now includes models with fewer than minJudgments, but sorted separately
 * @param minJudgments - Minimum number of judgments required for qualification
 * @param prioritySetting - Optional priority filter (speed, intelligence, balance, free, custom)
 * @param activeModels - Optional list of currently active model IDs to filter by
 */
export function useAllModelMajorityJudgments(
  minJudgments: number = 3,
  prioritySetting?: string,
  activeModels?: string[]
): ModelMajorityJudgmentData[] {
  const { user } = useAuth();
  const [models, setModels] = useState<ModelMajorityJudgmentData[]>([]);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    // Local mounted flag for this effect instance
    let isMounted = true;

    // Immediately cancel any existing subscription before starting a new one
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    if (!user) {
      setModels([]);
      return undefined;
    }

    // Subscribe to user's model grades collection
    const userGradesRef = collection(db, 'users', user.uid, 'modelGrades');
    
    const unsubscribe = onSnapshot(userGradesRef, (snapshot) => {
      // Only update state if still mounted
      if (!isMounted) return;

      let modelData: ModelMajorityJudgmentData[] = [];

      snapshot.docs.forEach(doc => {
        const data = doc.data();
        const mj = readMajorityJudgment(data) as MajorityJudgment;
        const docPriority = data.prioritySetting;
        
        // CRITICAL: Filter out zeroed-out documents (N === 0 or numJudgments === 0)
        // These are documents that were zeroed when all conversations were inactivated
        const numJudgments = mj?.judgmentsNumber ?? 0;
        if (numJudgments === 0) {
          return; // Skip zeroed-out documents
        }
        
        // Filter by priority setting and include ALL models, even with < minJudgments
        // SMART FILTERING:
        // - If no priority requested: show all docs
        // - If doc has no priority field: show it (backward compat - treat as matching any priority)
        // - If doc has priority field: ONLY show if it matches requested priority
        const shouldInclude = mj && (
          !prioritySetting ||                    // No filter requested
          !docPriority ||                         // Doc has no priority (legacy data)
          docPriority === prioritySetting         // Priorities match
        );
        
        // Debug: Log filtering decision for mismatches
        if (prioritySetting && docPriority && docPriority !== prioritySetting) {
          console.log('🚫 useAllModelMajorityJudgments: Filtering out model', {
            modelId: data.modelId,
            docPriority,
            requestedPriority: prioritySetting,
            shouldInclude
          });
        }
        
        if (shouldInclude) {
          modelData.push({
            modelId: data.modelId,
            userJudgment: mj,
            globalJudgment: null,
            lastUpdated: data.lastUpdated?.toDate() || null,
          });
        }
      });

      // Filter out ionoff models (they only appear in self-eval leaderboard)
      const beforeIonoffFilter = modelData.length;
      modelData = modelData.filter(m => !m.modelId.startsWith('ionoff'));
      
      console.log('📊 useAllModelMajorityJudgments: Data processing', {
        prioritySetting,
        totalDocs: snapshot.docs.length,
        afterPriorityFilter: beforeIonoffFilter,
        afterIonoffFilter: modelData.length,
        modelIds: modelData.map(m => m.modelId),
        sampleDoc: snapshot.docs[0]?.data()
      });

      // For user-level leaderboard, show ALL models (don't filter by activeModels)
      // User-level shows historical performance across all conversations

      // Sort by majority judgment (best first)
      modelData.sort((a, b) => {
        const mjA = a.userJudgment!;
        const mjB = b.userJudgment!;
        
        // First sort by whether they meet minimum judgments (qualified models first)
        const aQualified = (mjA.judgmentsNumber ?? 0) >= minJudgments;
        const bQualified = (mjB.judgmentsNumber ?? 0) >= minJudgments;
        
        if (aQualified !== bQualified) {
          return bQualified ? 1 : -1;
        }
        
        // Use Balinski gauge tie-breaking for qualified models
        const mjComparison = compareMajorityJudgments(mjA, mjB);
        if (mjComparison !== 0) return mjComparison;
        
        // Final tie-breaker: alphabetical by modelId (deterministic)
        return a.modelId.localeCompare(b.modelId);
      });

      setModels(modelData);
    });

    // Store the unsubscribe function
    unsubscribeRef.current = unsubscribe;

    return () => {
      isMounted = false;
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [user, minJudgments, prioritySetting, activeModels]);

  return models;
}

/**
 * Hook to get majority judgments for all models in a conversation (Level 1)
 * @param conversationId - Conversation ID to fetch judgments for
 * @param minJudgments - Minimum number of judgments required for qualification
 * @param prioritySetting - Optional priority filter (speed, intelligence, balance, free, custom)
 * @param activeModels - Optional list of currently active model IDs to filter by
 * @param localGradeState - Optional local grade state (for client-side processing)
 */
export function useConversationModelMajorityJudgments(
  conversationId: string | null,
  minJudgments: number = 3,
  prioritySetting?: string,
  activeModels?: string[],
  localGradeState?: any // ConversationGradeState from localGradeProcessor
): ModelMajorityJudgmentData[] {
  const [models, setModels] = useState<ModelMajorityJudgmentData[]>([]);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  
  // Create a stable key that changes only when the actual data changes
  // CRITICAL: Include priority in key to trigger recalculation when priority changes
  const validPrioritiesForKey = ['free', 'speed', 'balance', 'value', 'intelligence', 'custom'] as const;
  const getPriorityBucketSize = (state: any, priority: string | undefined) => {
    if (priority && validPrioritiesForKey.includes(priority as any)) {
      return state.buckets?.[priority]?.modelGrades?.size || 0;
    }
    return state.thisConversationCombined?.modelGrades?.size || 0;
  };
  const localGradeStateKey = localGradeState ? 
    `${prioritySetting || 'combined'}-${getPriorityBucketSize(localGradeState, prioritySetting)}-${localGradeState.lastUpdated?.getTime() || 0}` : 
    null;

  useEffect(() => {
    // NEW: If local state is available, use it immediately (no Firestore)
    const currentLocalState = localGradeState;
    if (currentLocalState) {
      // CRITICAL: Check if local state belongs to current conversation
      // If conversationId is defined AND different, ignore stale data
      if (currentLocalState.conversationId && currentLocalState.conversationId !== conversationId) {
        console.log('⚠️ useConversationModelMajorityJudgments: Ignoring stale local state', {
          expected: conversationId,
          actual: currentLocalState.conversationId
        });
        setModels([]);
        return; // Wait for correct data
      }
      
      // CRITICAL FIX: When prioritySetting is specified, use the priority-specific bucket
      // When no prioritySetting, use the combined bucket (shows all priorities)
      const validPriorities = ['free', 'speed', 'balance', 'value', 'intelligence', 'custom'] as const;
      type ValidPriority = typeof validPriorities[number];
      const isValidPriority = (p: string | undefined): p is ValidPriority => 
        !!p && validPriorities.includes(p as ValidPriority);
      
      const useFilteredBucket = isValidPriority(prioritySetting);
      const sourceGrades = useFilteredBucket 
        ? currentLocalState.buckets[prioritySetting].modelGrades
        : currentLocalState.thisConversationCombined.modelGrades;
      
      console.log('🔍 useConversationModelMajorityJudgments: Using local state', {
        conversationId,
        prioritySetting,
        useFilteredBucket,
        activeModels,
        minJudgments,
        modelGradesSize: sourceGrades?.size,
        localGradeStateKey,
        timestamp: currentLocalState.lastUpdated?.getTime()
      });
      
      const entries: Array<[string, any]> = Array.from(sourceGrades.entries());
      let modelData = entries
        .map(([modelId, agg]) => ({
          modelId,
          userJudgment: agg.majorityJudgment,
          globalJudgment: null,
          lastUpdated: agg.lastUpdated
        }));
      
      // Filter out ionoff models
      modelData = modelData.filter(m => !m.modelId.startsWith('ionoff'));
      
      console.log('🔍 useConversationModelMajorityJudgments: Using ' + (useFilteredBucket ? `priority bucket [${prioritySetting}]` : 'combined bucket (cross-priority)'), {
        count: modelData.length,
        models: modelData.map(m => ({ 
          id: m.modelId, 
          judgmentsNumber: m.userJudgment?.judgmentsNumber 
        }))
      });
      
      // NO SORTING HERE! The modelGrades Map from localGradeProcessor is already sorted correctly
      // (with MJ comparison + message-index tie-breaking + random tie-breaking).
      // JavaScript Maps preserve insertion order, so we just use it as-is.
      
      setModels(modelData);
      return; // Skip Firestore listener
    }
    
    // Fallback: Use Firestore (when local processing is disabled or on page refresh)
    let isMounted = true;

    // Immediately cancel any existing subscription before starting a new one
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    if (!conversationId) {
      setModels([]);
      return undefined;
    }

    // Subscribe to conversation's model grades collection
    const conversationGradesRef = collection(
      db,
      'conversations',
      conversationId,
      'modelGrades'
    );

    const unsubscribe = onSnapshot(
      conversationGradesRef,
      (snapshot) => {
        // Only update state if still mounted
        if (!isMounted) return;

        let modelData: ModelMajorityJudgmentData[] = [];

        snapshot.docs.forEach((doc) => {
          const data = doc.data();
          const mj = readMajorityJudgment(data) as MajorityJudgment;
          const docPriority = data.prioritySetting;

          // Filter by priority setting: only include docs that match the requested priority
          // STRICT FILTERING: If user has selected a priority, ONLY show docs with that exact priority
        if (mj && (!prioritySetting || docPriority === prioritySetting)) {
            modelData.push({
              modelId: data.modelId,
              userJudgment: mj,
              globalJudgment: null,
              lastUpdated: data.lastUpdated?.toDate() || null,
            });
          }
        });

        // Filter out ionoff models (they only appear in self-eval leaderboard)
        modelData = modelData.filter(m => !m.modelId.startsWith('ionoff'));

        // For conversation-level leaderboard, show ALL models that received grades
        // Don't filter by activeModels - models may have been swapped out mid-conversation
        // (activeModels filter is only used for user/global leaderboards)

        // Sort by majority judgment
        modelData.sort((a, b) => {
          const mjA = a.userJudgment!;
          const mjB = b.userJudgment!;

          const aQualified = (mjA.judgmentsNumber ?? 0) >= minJudgments;
          const bQualified = (mjB.judgmentsNumber ?? 0) >= minJudgments;

          if (aQualified !== bQualified) {
            return bQualified ? 1 : -1;
          }

          // Use Balinski gauge tie-breaking for qualified models
          const mjComparison = compareMajorityJudgments(mjA, mjB);
          if (mjComparison !== 0) return mjComparison;
          
          // Final tie-breaker: alphabetical by modelId (deterministic)
          return a.modelId.localeCompare(b.modelId);
        });

        setModels(modelData);
      },
      (error) => {
        // Gracefully handle permission errors and other Firestore errors
        console.warn(`Failed to load conversation-level majority judgments for ${conversationId}:`, error.message);
        if (isMounted) {
          setModels([]); // Clear models on error
        }
      }
    );

    // Store the unsubscribe function
    unsubscribeRef.current = unsubscribe;

    // Cleanup function
    return () => {
      isMounted = false;
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [conversationId, minJudgments, prioritySetting, activeModels, localGradeStateKey]); // Added localGradeStateKey to trigger updates

  return models;
}

/**
 * Hook to get majority judgments for all models globally (Level 3)
 * @param minJudgments - Minimum number of judgments required for qualification
 * @param prioritySetting - Optional priority filter (speed, intelligence, balance, free, custom)
 * @param activeModels - Optional list of currently active model IDs to filter by
 */
export function useGlobalModelMajorityJudgments(
  minJudgments: number = 3,
  prioritySetting?: string,
  activeModels?: string[]
): ModelMajorityJudgmentData[] {
  const { user } = useAuth();
  const [models, setModels] = useState<ModelMajorityJudgmentData[]>([]);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    // Local mounted flag for this effect instance
    let isMounted = true;

    // Immediately cancel any existing subscription before starting a new one
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    // Don't set up listener until user is authenticated
    if (!user) {
      setModels([]);
      return undefined;
    }

    // Subscribe to global model grades collection
    // Backend writes to top-level "globalModelGrades" collection
    const globalGradesRef = collection(db, 'globalModelGrades');

    const unsubscribe = onSnapshot(globalGradesRef, (snapshot) => {
      // Only update state if still mounted
      if (!isMounted) return;

      let modelData: ModelMajorityJudgmentData[] = [];

      snapshot.docs.forEach((doc) => {
        const data = doc.data();
        const mj = readMajorityJudgment(data) as MajorityJudgment;
        const docPriority = data.prioritySetting;

        // CRITICAL: Filter out zeroed-out documents (N === 0 or numJudgments === 0)
        // These are documents that were zeroed when all conversations were inactivated
        const numJudgments = mj?.judgmentsNumber ?? 0;
        if (numJudgments === 0) {
          return; // Skip zeroed-out documents
        }

        // Filter by priority setting
        // STRICT FILTERING: If user has selected a priority, ONLY show docs with that exact priority
        if (mj && (!prioritySetting || docPriority === prioritySetting)) {
          modelData.push({
            modelId: data.modelId,
            userJudgment: null,
            globalJudgment: mj,
            lastUpdated: data.lastUpdated?.toDate() || null,
          });
        }
      });

      // Filter out ionoff models (they only appear in self-eval leaderboard)
      modelData = modelData.filter(m => !m.modelId.startsWith('ionoff'));

      // Client-side filtering by active models if provided
      if (activeModels && activeModels.length > 0) {
        modelData = modelData.filter(m => 
          activeModels.includes(m.modelId) ||
          activeModels.some(am => 
            m.modelId.replace(/ \((high|low)\)$/, '') === am.replace(/ \((high|low)\)$/, '')
          )
        );
      }

      // Sort by majority judgment
      modelData.sort((a, b) => {
        const mjA = a.globalJudgment!;
        const mjB = b.globalJudgment!;

        // For global leaderboard, filter by promptsNumber (prompts) not judgmentsNumber (judgments)
        const aQualified = (mjA.promptsNumber ?? 0) >= minJudgments;
        const bQualified = (mjB.promptsNumber ?? 0) >= minJudgments;

        if (aQualified !== bQualified) {
          return bQualified ? 1 : -1;
        }

        // Use Balinski gauge tie-breaking for qualified models
        const mjComparison = compareMajorityJudgments(mjA, mjB);
        if (mjComparison !== 0) return mjComparison;
        
        // Final tie-breaker: alphabetical by modelId (deterministic)
        return a.modelId.localeCompare(b.modelId);
      });

      setModels(modelData);
    });

    // Store the unsubscribe function
    unsubscribeRef.current = unsubscribe;

    return () => {
      isMounted = false;
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [user, minJudgments, prioritySetting, activeModels]);

  return models;
}

/**
 * Format majority judgment for tooltip display
 * Format: majority judgment: [description] (N/M judgments)
 * Where N = number of judgments, M = number of prompts
 */
export function formatMajorityJudgmentTooltip(
  userJudgment: MajorityJudgment | null,
  globalJudgment: MajorityJudgment | null,
  gradeDescriptions: GradeDescriptions | null = null
): string {
  if (!userJudgment && !globalJudgment) {
    return 'No judgments yet';
  }

  const parts: string[] = [];

  // The majority grade with its gauge sign (Balinski–Laraki): "B+" means
  // more grades above B than below, "B-" the reverse. Two models can share
  // the letter and be stacked by the sign, so the sign is shown with it.
  const signedGrade = (mj: MajorityJudgment): string =>
    (mj.gradeCounts && formatMajorityGrade(mj.gradeCounts)) || mj.medianGrade;

  // User-level judgment
  const userJudgmentsNum = userJudgment?.judgmentsNumber ?? 0;
  if (userJudgment && userJudgmentsNum > 0) {
    const userPromptsNum = userJudgment?.promptsNumber ?? 0;
    const description = gradeDescriptions?.[userJudgment.medianGrade] || '';
    parts.push(
      `majority judgment: ${signedGrade(userJudgment)} ${description} (${userJudgmentsNum}/${userPromptsNum} judgments)`.replace(/\s+\(/, ' (')
    );
  }

  // Global-level judgment
  const globalJudgmentsNum = globalJudgment?.judgmentsNumber ?? 0;
  if (globalJudgment && globalJudgmentsNum > 0) {
    const globalPromptsNum = globalJudgment?.promptsNumber ?? 0;
    const description = gradeDescriptions?.[globalJudgment.medianGrade] || '';
    parts.push(
      `majority judgment: ${signedGrade(globalJudgment)} ${description} (${globalJudgmentsNum}/${globalPromptsNum} judgments)`.replace(/\s+\(/, ' (')
    );
  }

  return parts.join('\n');
}

/**
 * Hook to preload grade descriptions for tooltip display
 */
export function useGradeDescriptions(): GradeDescriptions | null {
  const [descriptions, setDescriptions] = useState<GradeDescriptions | null>(null);

  useEffect(() => {
    getGradeDescriptions()
      .then(setDescriptions)
      .catch((error) => {
        console.error('Error loading grade descriptions:', error);
      });
  }, []);

  return descriptions;
}

/**
 * CREATOR-LEVEL hooks (aggregate by creator prefix)
 */

export interface CreatorMajorityJudgmentData {
  creatorPrefix: string;
  userJudgment: MajorityJudgment | null;
  globalJudgment: MajorityJudgment | null;
  lastUpdated: Date | null;
}

/**
 * Hook to get creator-level majority judgments for a conversation (Level 1)
 * @param conversationId - Conversation ID to fetch judgments for
 * @param minJudgments - Minimum number of judgments required for qualification
 * @param prioritySetting - Optional priority filter (speed, intelligence, balance, free, custom)
 * @param localGradeState - Optional local grade state (for client-side processing)
 */
export function useConversationCreatorMajorityJudgments(
  conversationId: string | null,
  minJudgments: number = 3,
  prioritySetting?: string,
  localGradeState?: any // ConversationGradeState from localGradeProcessor
): CreatorMajorityJudgmentData[] {
  const [creators, setCreators] = useState<CreatorMajorityJudgmentData[]>([]);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  
  // Create a stable key that changes only when the actual data changes
  // CRITICAL: Include priority in key to trigger recalculation when priority changes
  const validPrioritiesForKey = ['free', 'speed', 'balance', 'value', 'intelligence', 'custom'] as const;
  const getPriorityBucketSize = (state: any, priority: string | undefined) => {
    if (priority && validPrioritiesForKey.includes(priority as any)) {
      return state.buckets?.[priority]?.creatorGrades?.size || 0;
    }
    return state.thisConversationCombined?.creatorGrades?.size || 0;
  };
  const localGradeStateKey = localGradeState ? 
    `${prioritySetting || 'combined'}-${getPriorityBucketSize(localGradeState, prioritySetting)}-${localGradeState.lastUpdated?.getTime() || 0}` : 
    null;

  useEffect(() => {
    // NEW: If local state is available, use it immediately (no Firestore)
    const currentLocalState = localGradeState;
    if (currentLocalState) {
      // CRITICAL: Check if local state belongs to current conversation
      // If conversationId is defined AND different, ignore stale data
      if (currentLocalState.conversationId && currentLocalState.conversationId !== conversationId) {
        console.log('⚠️ useConversationCreatorMajorityJudgments: Ignoring stale local state', {
          expected: conversationId,
          actual: currentLocalState.conversationId
        });
        setCreators([]);
        return; // Wait for correct data
      }
      
      // CRITICAL FIX: When prioritySetting is specified, use the priority-specific bucket
      // When no prioritySetting, use the combined bucket (shows all priorities)
      const validPriorities = ['free', 'speed', 'balance', 'value', 'intelligence', 'custom'] as const;
      type ValidPriority = typeof validPriorities[number];
      const isValidPriority = (p: string | undefined): p is ValidPriority => 
        !!p && validPriorities.includes(p as ValidPriority);
      
      const useFilteredBucket = isValidPriority(prioritySetting);
      const sourceGrades = useFilteredBucket 
        ? currentLocalState.buckets[prioritySetting].creatorGrades
        : currentLocalState.thisConversationCombined.creatorGrades;
      
      console.log('🔍 useConversationCreatorMajorityJudgments: Using local state', {
        conversationId,
        prioritySetting,
        useFilteredBucket,
        creatorGradesSize: sourceGrades?.size,
        localGradeStateKey,
        timestamp: currentLocalState.lastUpdated?.getTime()
      });
      
      const entries: Array<[string, any]> = Array.from(sourceGrades.entries());
      
      // DEBUG: Log what we have BEFORE filtering
      console.log('🔍 useConversationCreatorMajorityJudgments: Entries before filter', {
        count: entries.length,
        entries: entries.map(([prefix, agg]) => ({
          prefix,
          hasMJ: !!agg.majorityJudgment,
          grades: agg.grades?.length || 0
        }))
      });
      
      let creatorData = entries
        .filter(([, agg]) => {
          // Include creators as soon as they have a majorityJudgment object
          if (!agg.majorityJudgment) return false;
          return true;
        })
        .map(([creatorPrefix, agg]) => ({
          creatorPrefix,
          userJudgment: agg.majorityJudgment,
          globalJudgment: null,
          lastUpdated: agg.lastUpdated
        }));
      
      console.log('🔍 useConversationCreatorMajorityJudgments: Using ' + (useFilteredBucket ? `priority bucket [${prioritySetting}]` : 'combined bucket (cross-priority)'), {
        count: creatorData.length,
        creators: creatorData.map(c => c.creatorPrefix)
      });
      
      // Filter out ionoff creator
      creatorData = creatorData.filter(c => c.creatorPrefix !== 'ionoff');
      
      console.log('🔍 useConversationCreatorMajorityJudgments: After ionoff filter', {
        count: creatorData.length,
        creators: creatorData.map(c => c.creatorPrefix)
      });
      
      // Sort by majority judgment
      creatorData.sort((a, b) => {
        const mjA = a.userJudgment!;
        const mjB = b.userJudgment!;
        const aQualified = (mjA.judgmentsNumber ?? 0) >= minJudgments;
        const bQualified = (mjB.judgmentsNumber ?? 0) >= minJudgments;
        if (aQualified !== bQualified) return bQualified ? 1 : -1;
        const mjComparison = compareMajorityJudgments(mjA, mjB);
        if (mjComparison !== 0) return mjComparison;
        // Final tie-breaker: alphabetical by creatorPrefix (deterministic)
        return a.creatorPrefix.localeCompare(b.creatorPrefix);
      });
      
      setCreators(creatorData);
      return; // Skip Firestore listener
    }
    
    // Fallback: Use Firestore
    let isMounted = true;

    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    if (!conversationId) {
      setCreators([]);
      return undefined;
    }

    const conversationGradesRef = collection(
      db,
      'conversations',
      conversationId,
      'creatorGrades'
    );

    const unsubscribe = onSnapshot(
      conversationGradesRef,
      (snapshot) => {
        if (!isMounted) return;

        const creatorData: CreatorMajorityJudgmentData[] = [];

        snapshot.docs.forEach((doc) => {
          const data = doc.data();
          const mj = readMajorityJudgment(data) as MajorityJudgment;
          const docPriority = data.prioritySetting;

          // Filter by priority setting
          // STRICT FILTERING: If user has selected a priority, ONLY show docs with that exact priority
        if (mj && (!prioritySetting || docPriority === prioritySetting)) {
            creatorData.push({
              creatorPrefix: data.creatorPrefix,
              userJudgment: mj,
              globalJudgment: null,
              lastUpdated: data.lastUpdated?.toDate() || null,
            });
          }
        });

        // Filter out ionoff creator (only appears in self-eval leaderboard)
        const filteredCreatorData = creatorData.filter(c => c.creatorPrefix !== 'ionoff');

        // Sort by majority judgment
        filteredCreatorData.sort((a, b) => {
          const mjA = a.userJudgment!;
          const mjB = b.userJudgment!;

          const aQualified = (mjA.judgmentsNumber ?? 0) >= minJudgments;
          const bQualified = (mjB.judgmentsNumber ?? 0) >= minJudgments;

          if (aQualified !== bQualified) {
            return bQualified ? 1 : -1;
          }

          // Use Balinski gauge tie-breaking for qualified models
          const mjComparison = compareMajorityJudgments(mjA, mjB);
          if (mjComparison !== 0) return mjComparison;
          
          // Final tie-breaker: alphabetical by creatorPrefix (deterministic)
          return a.creatorPrefix.localeCompare(b.creatorPrefix);
        });

        setCreators(filteredCreatorData);
      },
      (error) => {
        console.warn(`Failed to load conversation-level creator judgments for ${conversationId}:`, error.message);
        if (isMounted) {
          setCreators([]);
        }
      }
    );

    unsubscribeRef.current = unsubscribe;

    return () => {
      isMounted = false;
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [conversationId, minJudgments, prioritySetting, localGradeStateKey]); // Added localGradeStateKey to trigger updates

  return creators;
}

/**
 * Hook to get user-level creator majority judgments (Level 2)
 * @param minJudgments - Minimum number of judgments required for qualification
 * @param prioritySetting - Optional priority filter (speed, intelligence, balance, free, custom)
 */
export function useUserCreatorMajorityJudgments(
  minJudgments: number = 3,
  prioritySetting?: string
): CreatorMajorityJudgmentData[] {
  const { user } = useAuth();
  const [creators, setCreators] = useState<CreatorMajorityJudgmentData[]>([]);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    let isMounted = true;

    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    if (!user) {
      setCreators([]);
      return undefined;
    }

    const userGradesRef = collection(db, 'users', user.uid, 'creatorGrades');
    
    const unsubscribe = onSnapshot(userGradesRef, (snapshot) => {
      if (!isMounted) return;

      const creatorData: CreatorMajorityJudgmentData[] = [];

      snapshot.docs.forEach(doc => {
        const data = doc.data();
        const mj = readMajorityJudgment(data) as MajorityJudgment;
        const docPriority = data.prioritySetting;
        
        // CRITICAL: Filter out zeroed-out documents (N === 0 or numJudgments === 0)
        // These are documents that were zeroed when all conversations were inactivated
        const numJudgments = mj?.judgmentsNumber ?? 0;
        if (numJudgments === 0) {
          return; // Skip zeroed-out documents
        }
        
        // Filter by priority setting
        // STRICT FILTERING: If user has selected a priority, ONLY show docs with that exact priority
        if (mj && (!prioritySetting || docPriority === prioritySetting)) {
          creatorData.push({
            creatorPrefix: data.creatorPrefix,
            userJudgment: mj,
            globalJudgment: null,
            lastUpdated: data.lastUpdated?.toDate() || null,
          });
        }
      });

      // Filter out ionoff creator (only appears in self-eval leaderboard)
      const filteredCreatorData = creatorData.filter(c => c.creatorPrefix !== 'ionoff');
      
      console.log('📊 useUserCreatorMajorityJudgments: Data processing', {
        prioritySetting,
        totalDocs: snapshot.docs.length,
        afterPriorityFilter: creatorData.length,
        afterIonoffFilter: filteredCreatorData.length,
        creatorPrefixes: filteredCreatorData.map(c => c.creatorPrefix),
        sampleDoc: snapshot.docs[0]?.data()
      });

      // Sort by majority judgment
      filteredCreatorData.sort((a, b) => {
        const mjA = a.userJudgment!;
        const mjB = b.userJudgment!;
        
        const aQualified = (mjA.judgmentsNumber ?? 0) >= minJudgments;
        const bQualified = (mjB.judgmentsNumber ?? 0) >= minJudgments;
        
        if (aQualified !== bQualified) {
          return bQualified ? 1 : -1;
        }
        
        // Use Balinski gauge tie-breaking for qualified models
        const mjComparison = compareMajorityJudgments(mjA, mjB);
        if (mjComparison !== 0) return mjComparison;
        
        // Final tie-breaker: alphabetical by creatorPrefix (deterministic)
        return a.creatorPrefix.localeCompare(b.creatorPrefix);
      });

      setCreators(filteredCreatorData);
    });

    unsubscribeRef.current = unsubscribe;

    return () => {
      isMounted = false;
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [user, minJudgments, prioritySetting]);

  return creators;
}

/**
 * Hook to get global creator majority judgments (Level 3)
 * @param minJudgments - Minimum number of judgments required for qualification
 * @param prioritySetting - Optional priority filter (speed, intelligence, balance, free, custom)
 */
export function useGlobalCreatorMajorityJudgments(
  minJudgments: number = 3,
  prioritySetting?: string
): CreatorMajorityJudgmentData[] {
  const { user } = useAuth();
  const [creators, setCreators] = useState<CreatorMajorityJudgmentData[]>([]);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    let isMounted = true;

    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    // Don't set up listener until user is authenticated
    if (!user) {
      setCreators([]);
      return undefined;
    }

    const globalGradesRef = collection(db, 'globalCreatorGrades');

    const unsubscribe = onSnapshot(globalGradesRef, (snapshot) => {
      if (!isMounted) return;

      const creatorData: CreatorMajorityJudgmentData[] = [];

      snapshot.docs.forEach((doc) => {
        const data = doc.data();
        const mj = readMajorityJudgment(data) as MajorityJudgment;
        const docPriority = data.prioritySetting;

        // CRITICAL: Filter out zeroed-out documents (N === 0 or numJudgments === 0)
        // These are documents that were zeroed when all conversations were inactivated
        const numJudgments = mj?.judgmentsNumber ?? 0;
        if (numJudgments === 0) {
          return; // Skip zeroed-out documents
        }

        // Filter by priority setting
        // STRICT FILTERING: If user has selected a priority, ONLY show docs with that exact priority
        if (mj && (!prioritySetting || docPriority === prioritySetting)) {
          creatorData.push({
            creatorPrefix: data.creatorPrefix,
            userJudgment: null,
            globalJudgment: mj,
            lastUpdated: data.lastUpdated?.toDate() || null,
          });
        }
      });

      // Filter out ionoff creator (only appears in self-eval leaderboard)
      const filteredCreatorData = creatorData.filter(c => c.creatorPrefix !== 'ionoff');

      // Sort by majority judgment
      filteredCreatorData.sort((a, b) => {
        const mjA = a.globalJudgment!;
        const mjB = b.globalJudgment!;

        // For global leaderboard, filter by promptsNumber (prompts) not judgmentsNumber (judgments)
        const aQualified = (mjA.promptsNumber ?? 0) >= minJudgments;
        const bQualified = (mjB.promptsNumber ?? 0) >= minJudgments;

        if (aQualified !== bQualified) {
          return bQualified ? 1 : -1;
        }

        // Use Balinski gauge tie-breaking for qualified models
        const mjComparison = compareMajorityJudgments(mjA, mjB);
        if (mjComparison !== 0) return mjComparison;
        
        // Final tie-breaker: alphabetical by creatorPrefix (deterministic)
        return a.creatorPrefix.localeCompare(b.creatorPrefix);
      });

      setCreators(filteredCreatorData);
    });

    unsubscribeRef.current = unsubscribe;

    return () => {
      isMounted = false;
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [user, minJudgments, prioritySetting]);

  return creators;
}

/**
 * SELF-EVAL LEADERBOARD HOOK
 */

export interface SelfEvalMajorityJudgmentData {
  modelId: string; // Can be 'ionoff', 'best', or synthesis variant IDs like 'ionoff-grading-...'
  majorityJudgment: MajorityJudgment;
  prioritySetting?: string;
  lastUpdated: Date | null;
  ionoffEquals?: boolean; // True if this synthesis variant is the final ionoff
}

/**
 * Hook to get conversation-level self-eval majority judgments for ionoff vs best
 * @param conversationId - Conversation ID
 * @param prioritySetting - Optional priority filter to match current setting
 * @param localGradeState - Optional local grade state (for client-side processing)
 */
export function useConversationSelfEvalMajorityJudgments(
  conversationId: string | null,
  prioritySetting?: string,
  localGradeState?: any, // ConversationGradeState from localGradeProcessor
  messages?: any[] // Optional messages array to parse matrix rankings
): SelfEvalMajorityJudgmentData[] {
  const [data, setData] = useState<SelfEvalMajorityJudgmentData[]>([]);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  
  // Create a stable key that changes only when the actual data changes
  // CRITICAL: Include priority in key to trigger recalculation when priority changes
  const validPrioritiesForKey = ['free', 'speed', 'balance', 'value', 'intelligence', 'custom'] as const;
  const getSelfEvalBucketSize = (state: any, priority: string | undefined) => {
    if (priority && validPrioritiesForKey.includes(priority as any)) {
      return state.selfEvalGrades?.[priority]?.size || 0;
    }
    return state.selfEvalCombined?.size || 0;
  };
  const localGradeStateKey = localGradeState ? 
    `${prioritySetting || 'combined'}-${getSelfEvalBucketSize(localGradeState, prioritySetting)}-${localGradeState.lastUpdated?.getTime() || 0}` : 
    null;

  useEffect(() => {
    console.log('🔍 useConversationSelfEvalMajorityJudgments: Hook effect running', {
      conversationId,
      hasLocalGradeState: !!localGradeState,
      prioritySetting,
      hasMessages: !!messages,
      messagesCount: messages?.length || 0
    });
    
    // NEW: If local state is available, use it immediately (no Firestore)
    const currentLocalState = localGradeState;
    if (currentLocalState) {
      // CRITICAL FIX: When prioritySetting is specified, use the priority-specific bucket
      // When no prioritySetting, use the combined bucket (shows all priorities)
      const validPriorities = ['free', 'speed', 'balance', 'value', 'intelligence', 'custom'] as const;
      type ValidPriority = typeof validPriorities[number];
      const isValidPriority = (p: string | undefined): p is ValidPriority => 
        !!p && validPriorities.includes(p as ValidPriority);
      
      const useFilteredBucket = isValidPriority(prioritySetting);
      const sourceGrades = useFilteredBucket 
        ? currentLocalState.selfEvalGrades[prioritySetting]
        : currentLocalState.selfEvalCombined;
      
      console.log('🔍 useConversationSelfEvalMajorityJudgments: Using local state', {
        conversationId,
        localStateConversationId: currentLocalState.conversationId,
        selfEvalSize: sourceGrades.size,
        useFilteredBucket,
        prioritySetting,
        hasMessages: !!messages,
        messagesCount: messages?.length || 0
      });
      
      // CRITICAL: Check if local state belongs to current conversation
      // If not, ignore it and wait for the correct data to load
      // IMPORTANT: Only reject if conversationId is defined AND different
      // (undefined means old state format before conversationId was added - allow through for backwards compat)
      if (currentLocalState.conversationId && currentLocalState.conversationId !== conversationId) {
        console.log('⚠️ Local state conversation ID mismatch - ignoring stale data', {
          expected: conversationId,
          actual: currentLocalState.conversationId
        });
        setData([]);
        return; // Skip Firestore listener and wait for correct data
      }
      
      // CRITICAL: If the source bucket is empty, fall through to Firestore
      // This handles:
      // 1. Conversations genuinely without self-eval (Firestore will return empty)
      // 2. Temporary React state staleness (Firestore will have the real data)
      if (sourceGrades.size === 0) {
        console.log('🔧 No self-eval grades in local state' + (useFilteredBucket ? ` for priority [${prioritySetting}]` : '') + ' - falling through to Firestore');
        // Don't return early - let Firestore listener handle both cases
      } else {
      
      // STEP 1: Try to parse rankings from matrix message (source of truth)
      let matrixRankings: Array<{ rank: number; modelId: string; medianGrade: string }> | null = null;
      if (messages && messages.length > 0) {
        const matrixMessage = messages.find(m => 
          m.isGradeMatrix && 
          m.content?.includes('📊 **Self-Eval Grading Matrix**') &&
          m.content?.includes('**Final Rankings:**')
        );
        
        if (matrixMessage) {
          console.log('📊 Found self-eval matrix message, parsing rankings...');
          const rankingsMatch = matrixMessage.content.match(/\*\*Final Rankings:\*\*\n([\s\S]+?)(?:\n\n|$)/);
          if (rankingsMatch) {
            const rankingsSection = rankingsMatch[1];
            const rankingLines = rankingsSection.split('\n').filter((line: string) => line.trim());
            
            matrixRankings = rankingLines
              .map((line: string) => {
                // Parse line like: "1. [2] ionoff_a_final (synthesis-a-final) ("..."): **B/C** (BBCD)"
                const match = line.match(/^(\d+)\.\s+\[(\d+)\]\s+(\S+)(?:\s+\([^)]+\))?\s+\(".*?"\):\s+\*\*([A-F]\/?[A-F]?)\*\*\s+\([A-F]+\)/);
                if (match) {
                  const rank = parseInt(match[1], 10);
                  const modelId = match[3];
                  const medianGrade = match[4];
                  return { rank, modelId, medianGrade };
                }
                return null;
              })
              .filter((r: { rank: number; modelId: string; medianGrade: string } | null): r is { rank: number; modelId: string; medianGrade: string } => r !== null);
            
            console.log('✅ Parsed matrix rankings:', matrixRankings);
          }
        }
      }
      
      // STEP 2: Get base data from localGradeState (using priority-specific or combined bucket)
      const entries: Array<[string, any]> = Array.from(sourceGrades.entries());
      
      let selfEvalData = entries
        .map(([modelId, agg]) => ({
          modelId,
          majorityJudgment: agg.majorityJudgment,
          prioritySetting: agg.prioritySetting,
          lastUpdated: agg.lastUpdated,
          ionoffEquals: agg.ionoffEquals
        }));
      
      // STEP 3: If matrix rankings exist, use them to reorder the data
      // BUT preserve the ionoffEquals relationship: if a variant has ionoffEquals=true,
      // its duplicate ionoff-grading- entry should appear immediately after it
      if (matrixRankings && matrixRankings.length > 0) {
        console.log('🔄 Reordering based on matrix rankings...');
        const rankingMap = new Map(matrixRankings.map(r => [r.modelId, r.rank]));
        
        // Build map of ionoffEquals variants to their duplicate ionoff-grading- IDs
        const ionoffEqualsMap = new Map<string, string>(); // variant -> ionoff-grading-id
        const duplicateToVariantMap = new Map<string, string>(); // ionoff-grading-id -> variant
        
        // FIRST: Find all ionoff-grading- entries and match them to variants
        const allDuplicates = selfEvalData.filter(item => item.modelId.startsWith('ionoff-grading-'));
        // CRITICAL: Match to ALL variants (not just ones with ionoffEquals=true)
        // The duplicate ID tells us which variant it matches to, regardless of the flag
        const allVariants = selfEvalData.filter(item => item.modelId.startsWith('ionoff_'));
        
        console.log('🔍 Matching duplicates to variants:', {
          duplicates: allDuplicates.map(d => d.modelId),
          variants: allVariants.map(v => ({ id: v.modelId, ionoffEquals: v.ionoffEquals }))
        });
        
        // For each duplicate, find its matching variant by parsing the duplicate ID
        allDuplicates.forEach(duplicate => {
          // Extract variant name from duplicate ID: ionoff-grading-v1-synthesis-v1-new-plain -> new-plain
          const parts = duplicate.modelId.split('-');
          // Find the variant part after 'synthesis-v1-'
          const synthesisV1Index = parts.indexOf('synthesis');
          if (synthesisV1Index >= 0 && synthesisV1Index + 2 < parts.length) {
            const variantName = parts.slice(synthesisV1Index + 2).join('-'); // Everything after 'synthesis-'
            // Match to variant: new-plain -> ionoff_new_plain
            const variantId = `ionoff_${variantName.replace(/-/g, '_')}`;
            
            const matchingVariant = allVariants.find(v => v.modelId === variantId);
            if (matchingVariant) {
              ionoffEqualsMap.set(matchingVariant.modelId, duplicate.modelId);
              duplicateToVariantMap.set(duplicate.modelId, matchingVariant.modelId);
              console.log(`🔗 Found ionoffEquals pair: ${matchingVariant.modelId} -> ${duplicate.modelId}`, {
                variantHasFlag: matchingVariant.ionoffEquals === true
              });
            } else {
              console.warn('⚠️ Duplicate found but no matching variant:', {
                duplicateId: duplicate.modelId,
                extractedVariant: variantName,
                expectedVariantId: variantId,
                availableVariants: allVariants.map(v => v.modelId)
              });
            }
          } else {
            console.warn('⚠️ Could not parse duplicate ID:', {
              duplicateId: duplicate.modelId,
              parts,
              synthesisV1Index
            });
          }
        });
        
        // Helper function to get ranking: if duplicate not in matrix, inherit from variant
        const getRanking = (modelId: string): number => {
          let rank = rankingMap.get(modelId);
          if (rank !== undefined) {
            return rank;
          }
          // If this is a duplicate, try to inherit ranking from its variant
          const variantId = duplicateToVariantMap.get(modelId);
          if (variantId) {
            rank = rankingMap.get(variantId);
            if (rank !== undefined) {
              console.log(`📊 Duplicate ${modelId} inheriting rank ${rank} from variant ${variantId}`);
              return rank;
            }
          }
          return 999999; // Put unranked items at end
        };
        
        // Sort by matrix ranking (lower rank = better position)
        selfEvalData.sort((a, b) => {
          const rankA = getRanking(a.modelId);
          const rankB = getRanking(b.modelId);
          
          // If ranks are equal, check if one is the duplicate of the other (ionoffEquals relationship)
          if (rankA === rankB) {
            // Check if 'a' is a variant and 'b' is its duplicate
            const duplicateOfA = ionoffEqualsMap.get(a.modelId);
            if (duplicateOfA === b.modelId) {
              return -1; // Variant comes before its duplicate
            }
            // Check if 'b' is a variant and 'a' is its duplicate
            const duplicateOfB = ionoffEqualsMap.get(b.modelId);
            if (duplicateOfB === a.modelId) {
              return 1; // Variant comes before its duplicate
            }
          }
          
          return rankA - rankB;
        });
        
        // Post-process: FORCE ionoffEquals variants to be immediately followed by their duplicates
        // This is CRITICAL - regardless of rankings, the duplicate MUST be directly under the variant
        // Process from end to beginning to avoid index shifts
        for (let i = selfEvalData.length - 1; i >= 0; i--) {
          const current = selfEvalData[i];
          const duplicateId = ionoffEqualsMap.get(current.modelId);
          
          if (duplicateId) {
            // Find the duplicate's current position
            const duplicateIndex = selfEvalData.findIndex(item => item.modelId === duplicateId);
            
            if (duplicateIndex < 0) {
              console.warn(`⚠️ Duplicate ${duplicateId} not found in selfEvalData for variant ${current.modelId}`);
              continue;
            }
            
            // CRITICAL: If duplicate is not immediately after the variant, MOVE IT
            if (duplicateIndex !== i + 1) {
              // Remove duplicate from its current position
              const duplicate = selfEvalData.splice(duplicateIndex, 1)[0];
              
              // Calculate correct insertion index after removal
              // - If duplicate was BEFORE variant (duplicateIndex < i):
              //   After removal, variant shifts from i to i-1, so insert at i (was i+1 before removal)
              // - If duplicate was AFTER variant (duplicateIndex > i):
              //   After removal, variant stays at i, so insert at i+1
              let insertIndex: number;
              if (duplicateIndex < i) {
                insertIndex = i; // Variant is now at i-1, insert after it at i
              } else {
                insertIndex = i + 1; // Variant is still at i, insert after it at i+1
              }
              
              // Insert it immediately after the variant
              selfEvalData.splice(insertIndex, 0, duplicate);
              
              // Verify final positions
              const finalVariantIndex = selfEvalData.findIndex(item => item.modelId === current.modelId);
              const finalDuplicateIndex = selfEvalData.findIndex(item => item.modelId === duplicateId);
              
              console.log(`🔧 FORCED duplicate ${duplicateId} to immediately follow ${current.modelId}`, {
                variantModelId: current.modelId,
                duplicateModelId: duplicateId,
                variantOriginalIndex: i,
                duplicateOriginalIndex: duplicateIndex,
                insertIndex,
                finalVariantIndex,
                finalDuplicateIndex,
                isAdjacent: finalDuplicateIndex === finalVariantIndex + 1
              });
              
              // CRITICAL ASSERTION: Duplicate must be immediately after variant
              if (finalDuplicateIndex !== finalVariantIndex + 1) {
                console.error(`❌ FAILED: Duplicate is not immediately after variant!`, {
                  variantIndex: finalVariantIndex,
                  duplicateIndex: finalDuplicateIndex,
                  gap: finalDuplicateIndex - finalVariantIndex
                });
              }
            } else {
              console.log(`✅ Duplicate ${duplicateId} already immediately follows ${current.modelId}`);
            }
          }
        }
        
        console.log('✅ Reordered based on matrix (with ionoffEquals preservation):', {
        count: selfEvalData.length,
          order: selfEvalData.map(d => ({ 
            id: d.modelId, 
            rank: getRanking(d.modelId),
            inheritedRank: duplicateToVariantMap.has(d.modelId) && !rankingMap.has(d.modelId) 
              ? `from ${duplicateToVariantMap.get(d.modelId)}` 
              : undefined,
            grade: d.majorityJudgment.medianGrade,
            ionoffEquals: d.ionoffEquals
          }))
        });
      } else {
        // No matrix yet - use Map order (already sorted correctly by aggregateByModel)
        console.log('📊 No matrix found, using localGradeState order (already sorted correctly)');
      }
      
      // CRITICAL: Filter out ionoff-grading- duplicates when there's only ONE synthesis variant
      // This ensures consistency with user/global stacks (which don't store duplicates)
      // When multiple synthesis variants exist, we keep duplicates to show the relationship
      const synthesisVariants = selfEvalData.filter(item => item.modelId.startsWith('ionoff_'));
      const ionoffDuplicates = selfEvalData.filter(item => item.modelId.startsWith('ionoff-grading-'));
      
      let finalData = selfEvalData;
      if (synthesisVariants.length === 1 && ionoffDuplicates.length > 0) {
        // Single synthesis variant case: Remove the ionoff-grading- duplicate
        console.log('🔧 Single synthesis variant detected - filtering out ionoff-grading- duplicates', {
          synthesisVariant: synthesisVariants[0].modelId,
          duplicatesToRemove: ionoffDuplicates.map(d => d.modelId)
        });
        finalData = selfEvalData.filter(item => !item.modelId.startsWith('ionoff-grading-'));
      } else if (synthesisVariants.length > 1) {
        // Multiple synthesis variants case: Keep duplicates to show relationships
        console.log('🔧 Multiple synthesis variants detected - keeping ionoff-grading- duplicates', {
          synthesisVariants: synthesisVariants.map(v => v.modelId),
          duplicates: ionoffDuplicates.map(d => d.modelId)
        });
      }
      
      console.log('🔍 useConversationSelfEvalMajorityJudgments: Final data', {
        count: finalData.length,
        usedMatrixRankings: !!matrixRankings,
        order: finalData.map(d => ({ id: d.modelId, grade: d.majorityJudgment.medianGrade }))
      });
      
      setData(finalData);
        return; // Skip Firestore listener when local state has data
      }
    }
    
    // Fallback: Use Firestore (when no local state, or local state is empty)
    let isMounted = true;

    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    if (!conversationId) {
      setData([]);
      return undefined;
    }

    // Subscribe to conversation self-eval grades collection
    const selfEvalGradesRef = collection(db, 'conversations', conversationId, 'selfEvalGrades');

    const unsubscribe = onSnapshot(selfEvalGradesRef, (snapshot) => {
      if (!isMounted) return;

      console.log(`🔍 Self-eval snapshot received for conversation ${conversationId}:`, {
        totalDocs: snapshot.docs.length,
        docIds: snapshot.docs.map(d => d.id),
        prioritySetting,
      });

      const selfEvalData: SelfEvalMajorityJudgmentData[] = [];

      snapshot.docs.forEach((doc) => {
        const docData = doc.data();
        const mj = readMajorityJudgment(docData) as MajorityJudgment;
        const docPriority = docData.prioritySetting;

        console.log(`📊 Processing self-eval doc:`, {
          docId: doc.id,
          modelId: docData.modelId,
          docPriority,
          requestedPriority: prioritySetting,
          hasMJ: !!mj,
          medianGrade: mj?.medianGrade,
        });

        // STRICT FILTERING: If user has selected a priority, ONLY show docs with that exact priority
        if (mj && (!prioritySetting || docPriority === prioritySetting)) {
          // Use full doc.id as model ID (can be 'ionoff', 'best', or synthesis variant like 'ionoff-grading-...')
          const modelId = doc.id;
          console.log(`✅ Including self-eval doc: ${modelId}`);
          selfEvalData.push({
            modelId,
            majorityJudgment: mj,
            prioritySetting: docPriority,
            lastUpdated: docData.lastUpdated?.toDate() || null,
            ionoffEquals: docData.ionoffEquals || undefined, // CRITICAL: Read ionoffEquals flag from Firestore
          });
        } else {
          console.log(`❌ Excluding self-eval doc: ${doc.id} (priority mismatch or missing MJ)`);
        }
      });

      console.log(`📊 Final self-eval data (before positioning):`, {
        count: selfEvalData.length,
        modelIds: selfEvalData.map(d => d.modelId),
      });

      // Sort by majority judgment first, then by message index (same logic as local state)
      selfEvalData.sort((a, b) => {
        const mjComparison = compareMajorityJudgments(a.majorityJudgment, b.majorityJudgment);
        if (mjComparison !== 0) return mjComparison;
        
        // Tie-breaker 1: Message index (earlier message wins)
        // This matches the logic in localGradeProcessor.aggregateByModel
        if (messages && messages.length > 0) {
          const getMessageIndex = (modelId: string): number => {
            // Map special IDs to what we search for in messages
            if (modelId.startsWith('ionoff-grading-')) {
              // Versioned ionoff - extract variant and find matching synthesis message
              const parts = modelId.split('-');
              const synthesisV1Index = parts.indexOf('synthesis');
              if (synthesisV1Index >= 0 && synthesisV1Index + 2 < parts.length) {
                const variant = parts.slice(synthesisV1Index + 2).join('-');
                const idx = messages.findIndex(m => m.role === 'assistant' && m.synthesisVariant === variant);
                return idx >= 0 ? idx : 999999;
              }
              return 999999;
            } else if (modelId.startsWith('ionoff_')) {
              // Synthesis variant - find matching message
              const variant = modelId.replace('ionoff_', '').replace(/_/g, '-');
              const idx = messages.findIndex(m => m.role === 'assistant' && m.synthesisVariant === variant);
              return idx >= 0 ? idx : 999999;
            } else if (modelId === 'ionoff') {
              // Main synthesis
              const idx = messages.findIndex(m => m.role === 'assistant' && m.isSynthesized && !m.synthesisVariant);
              return idx >= 0 ? idx : 999999;
            } else if (modelId === 'best') {
              // Peer-review winner - first model response
              const idx = messages.findIndex(m => m.role === 'assistant' && m.modelId && !m.isSynthesized);
              return idx >= 0 ? idx : 999999;
            }
            return 999999;
          };
          
          const indexA = getMessageIndex(a.modelId);
          const indexB = getMessageIndex(b.modelId);
          
          if (indexA !== indexB) {
            console.log(`🔍 Firestore: Message index tie-breaker: ${a.modelId}[${indexA}] vs ${b.modelId}[${indexB}]`);
            return indexA - indexB;
          }
        }
        
        // Tie-breaker 2: Alphabetical by modelId (deterministic)
        return a.modelId.localeCompare(b.modelId);
      });

      // CRITICAL: Apply same ionoffEquals positioning logic as local state path
      // Build map of ionoffEquals variants to their duplicate ionoff-grading- IDs
      const ionoffEqualsMap = new Map<string, string>(); // variant -> ionoff-grading-id
      const allDuplicates = selfEvalData.filter(item => item.modelId.startsWith('ionoff-grading-'));
      // CRITICAL: Match to ALL variants (not just ones with ionoffEquals=true)
      // The duplicate ID tells us which variant it matches to, regardless of the flag
      const allVariants = selfEvalData.filter(item => item.modelId.startsWith('ionoff_'));
      
      console.log('🔍 Firestore path: Matching duplicates to variants:', {
        duplicates: allDuplicates.map(d => d.modelId),
        variants: allVariants.map(v => ({ id: v.modelId, ionoffEquals: v.ionoffEquals }))
      });
      
      // For each duplicate, find its matching variant by parsing the duplicate ID
      allDuplicates.forEach(duplicate => {
        const parts = duplicate.modelId.split('-');
        const synthesisV1Index = parts.indexOf('synthesis');
        if (synthesisV1Index >= 0 && synthesisV1Index + 2 < parts.length) {
          const variantName = parts.slice(synthesisV1Index + 2).join('-');
          const variantId = `ionoff_${variantName.replace(/-/g, '_')}`;
          
          const matchingVariant = allVariants.find(v => v.modelId === variantId);
          if (matchingVariant) {
            ionoffEqualsMap.set(matchingVariant.modelId, duplicate.modelId);
            console.log(`🔗 Firestore: Found ionoffEquals pair: ${matchingVariant.modelId} -> ${duplicate.modelId}`, {
              variantHasFlag: matchingVariant.ionoffEquals === true
            });
          } else {
            console.warn('⚠️ Firestore: Duplicate found but no matching variant:', {
              duplicateId: duplicate.modelId,
              extractedVariant: variantName,
              expectedVariantId: variantId,
              availableVariants: allVariants.map(v => v.modelId)
            });
          }
        } else {
          console.warn('⚠️ Firestore: Could not parse duplicate ID:', {
            duplicateId: duplicate.modelId,
            parts,
            synthesisV1Index
          });
        }
      });
      
      // Post-process: FORCE ionoffEquals variants to be immediately followed by their duplicates
      for (let i = selfEvalData.length - 1; i >= 0; i--) {
        const current = selfEvalData[i];
        const duplicateId = ionoffEqualsMap.get(current.modelId);
        
        if (duplicateId) {
          const duplicateIndex = selfEvalData.findIndex(item => item.modelId === duplicateId);
          
          if (duplicateIndex >= 0 && duplicateIndex !== i + 1) {
            const duplicate = selfEvalData.splice(duplicateIndex, 1)[0];
            const insertIndex = duplicateIndex < i ? i : i + 1;
            selfEvalData.splice(insertIndex, 0, duplicate);
            
            console.log(`🔧 Firestore: FORCED duplicate ${duplicateId} to immediately follow ${current.modelId}`, {
              variantModelId: current.modelId,
              duplicateModelId: duplicateId,
              variantOriginalIndex: i,
              duplicateOriginalIndex: duplicateIndex,
              insertIndex
            });
          }
        }
      }

      console.log(`📊 Final self-eval data (after positioning):`, {
        count: selfEvalData.length,
        modelIds: selfEvalData.map(d => d.modelId),
      });

      setData(selfEvalData);
    });

    unsubscribeRef.current = unsubscribe;

    return () => {
      isMounted = false;
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [conversationId, prioritySetting, localGradeStateKey, messages]); // Include messages to re-parse when matrix is added

  return data;
}

/**
 * Hook to get user-level self-eval majority judgments for ionoff vs best
 * Only includes grades where isSelfEval=true and isRetest=true
 * @param prioritySetting - Optional priority filter to match current setting
 */
export function useSelfEvalMajorityJudgments(
  prioritySetting?: string
): SelfEvalMajorityJudgmentData[] {
  const { user } = useAuth();
  const [data, setData] = useState<SelfEvalMajorityJudgmentData[]>([]);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    let isMounted = true;

    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    if (!user) {
      setData([]);
      return undefined;
    }

    // Subscribe to self-eval grades collection
    const selfEvalGradesRef = collection(db, 'users', user.uid, 'selfEvalGrades');

    const unsubscribe = onSnapshot(selfEvalGradesRef, (snapshot) => {
      if (!isMounted) return;

      const selfEvalData: SelfEvalMajorityJudgmentData[] = [];

      snapshot.docs.forEach((doc) => {
        const docData = doc.data();
        const mj = readMajorityJudgment(docData) as MajorityJudgment;
        const docPriority = docData.prioritySetting;

        // CRITICAL: Filter out zeroed-out documents (N === 0 or numJudgments === 0)
        const numJudgments = mj?.judgmentsNumber ?? 0;
        if (numJudgments === 0) {
          return; // Skip zeroed-out documents
        }

        // Filter by priority if specified
        // If no priority filter, or if document priority matches, include it
        // STRICT FILTERING: If user has selected a priority, ONLY show docs with that exact priority
        if (mj && (!prioritySetting || docPriority === prioritySetting)) {
          // Use full doc.id as model ID (can be 'ionoff', 'best', or synthesis variant like 'ionoff-grading-...')
          const modelId = doc.id;
          selfEvalData.push({
            modelId,
            majorityJudgment: mj,
            prioritySetting: docPriority,
            lastUpdated: docData.lastUpdated?.toDate() || null,
          });
        }
      });

      // 🎯 FIX: Consolidate ionoff variants for cleaner display
      // When there's only one synthesis variant being tested, show only "ionoff"
      const synthesisVariants = selfEvalData.filter(item => 
        item.modelId.startsWith('ionoff_')
      );
      const ionoffDuplicates = selfEvalData.filter(item => 
        item.modelId.startsWith('ionoff-grading-')
      );
      const existingIonoff = selfEvalData.filter(item => 
        item.modelId === 'ionoff'
      );
      
      let finalData = selfEvalData;
      
      // CASE 1: Already have 'ionoff' entry - filter out ionoff_ variants (old data coexisting)
      if (existingIonoff.length > 0 && synthesisVariants.length > 0) {
        console.log('🔧 [User Self-Eval] Existing ionoff found - filtering out ionoff_ variants', {
          existingIonoff: existingIonoff.length,
          variantsToRemove: synthesisVariants.map(v => v.modelId)
        });
        finalData = selfEvalData.filter(item => !item.modelId.startsWith('ionoff_'));
      }
      // CASE 2: Have ionoff-grading-* duplicates - filter out ionoff_ variants
      else if (synthesisVariants.length === 1 && ionoffDuplicates.length > 0) {
        console.log('🔧 [User Self-Eval] Single synthesis variant with duplicates - filtering out ionoff_ variants', {
          synthesisVariant: synthesisVariants[0].modelId,
          keeping: ionoffDuplicates.map(d => d.modelId)
        });
        finalData = selfEvalData.filter(item => !item.modelId.startsWith('ionoff_'));
      } 
      // CASE 3: Single synthesis variant, no duplicates, no existing ionoff - rename to "ionoff"
      else if (synthesisVariants.length === 1 && ionoffDuplicates.length === 0 && existingIonoff.length === 0) {
        console.log('🔧 [User Self-Eval] Single synthesis variant - renaming to ionoff', {
          originalVariant: synthesisVariants[0].modelId
        });
        finalData = selfEvalData.map(item => {
          if (item.modelId.startsWith('ionoff_')) {
            return { ...item, modelId: 'ionoff' };
          }
          return item;
        });
      } 
      // CASE 4: Multiple synthesis variants - keep all for comparison
      else if (synthesisVariants.length > 1) {
        console.log('🔧 [User Self-Eval] Multiple synthesis variants - keeping all', {
          variants: synthesisVariants.map(v => v.modelId)
        });
      }

      // Sort by majority judgment (ionoff vs best)
      finalData.sort((a, b) => {
        const mjComparison = compareMajorityJudgments(a.majorityJudgment, b.majorityJudgment);
        if (mjComparison !== 0) return mjComparison;
        // Final tie-breaker: alphabetical by modelId (deterministic)
        return a.modelId.localeCompare(b.modelId);
      });

      setData(finalData);
    });

    unsubscribeRef.current = unsubscribe;

    return () => {
      isMounted = false;
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [user, prioritySetting]);

  return data;
}

/**
 * Hook to get global-level self-eval majority judgments for ionoff vs best
 * @param prioritySetting - Optional priority filter to match current setting
 */
export function useGlobalSelfEvalMajorityJudgments(
  prioritySetting?: string
): SelfEvalMajorityJudgmentData[] {
  const { user } = useAuth();
  const [data, setData] = useState<SelfEvalMajorityJudgmentData[]>([]);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    let isMounted = true;

    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    // Don't set up listener until user is authenticated
    if (!user) {
      setData([]);
      return undefined;
    }

    // Subscribe to global self-eval grades collection
    const selfEvalGradesRef = collection(db, 'globalSelfEvalGrades');

    const unsubscribe = onSnapshot(selfEvalGradesRef, (snapshot) => {
      if (!isMounted) return;

      const selfEvalData: SelfEvalMajorityJudgmentData[] = [];

      snapshot.docs.forEach((doc) => {
        const docData = doc.data();
        const mj = readMajorityJudgment(docData) as MajorityJudgment;
        const docPriority = docData.prioritySetting;

        // CRITICAL: Filter out zeroed-out documents (N === 0 or numJudgments === 0)
        const numJudgments = mj?.judgmentsNumber ?? 0;
        if (numJudgments === 0) {
          return; // Skip zeroed-out documents
        }

        // STRICT FILTERING: If user has selected a priority, ONLY show docs with that exact priority
        if (mj && (!prioritySetting || docPriority === prioritySetting)) {
          // Use full doc.id as model ID (can be 'ionoff', 'best', or synthesis variant like 'ionoff-grading-...')
          const modelId = doc.id;
          selfEvalData.push({
            modelId,
            majorityJudgment: mj,
            prioritySetting: docPriority,
            lastUpdated: docData.lastUpdated?.toDate() || null,
          });
        }
      });

      // 🎯 FIX: Consolidate ionoff variants for cleaner display
      // When there's only one synthesis variant being tested, show only "ionoff"
      const synthesisVariants = selfEvalData.filter(item => 
        item.modelId.startsWith('ionoff_')
      );
      const ionoffDuplicates = selfEvalData.filter(item => 
        item.modelId.startsWith('ionoff-grading-')
      );
      const existingIonoff = selfEvalData.filter(item => 
        item.modelId === 'ionoff'
      );
      
      let finalData = selfEvalData;
      
      // CASE 1: Already have 'ionoff' entry - filter out ionoff_ variants (old data coexisting)
      if (existingIonoff.length > 0 && synthesisVariants.length > 0) {
        console.log('🔧 [Global Self-Eval] Existing ionoff found - filtering out ionoff_ variants', {
          existingIonoff: existingIonoff.length,
          variantsToRemove: synthesisVariants.map(v => v.modelId)
        });
        finalData = selfEvalData.filter(item => !item.modelId.startsWith('ionoff_'));
      }
      // CASE 2: Have ionoff-grading-* duplicates - filter out ionoff_ variants
      else if (synthesisVariants.length === 1 && ionoffDuplicates.length > 0) {
        console.log('🔧 [Global Self-Eval] Single synthesis variant with duplicates - filtering out ionoff_ variants', {
          synthesisVariant: synthesisVariants[0].modelId,
          keeping: ionoffDuplicates.map(d => d.modelId)
        });
        finalData = selfEvalData.filter(item => !item.modelId.startsWith('ionoff_'));
      } 
      // CASE 3: Single synthesis variant, no duplicates, no existing ionoff - rename to "ionoff"
      else if (synthesisVariants.length === 1 && ionoffDuplicates.length === 0 && existingIonoff.length === 0) {
        console.log('🔧 [Global Self-Eval] Single synthesis variant - renaming to ionoff', {
          originalVariant: synthesisVariants[0].modelId
        });
        finalData = selfEvalData.map(item => {
          if (item.modelId.startsWith('ionoff_')) {
            return { ...item, modelId: 'ionoff' };
          }
          return item;
        });
      } 
      // CASE 4: Multiple synthesis variants - keep all for comparison
      else if (synthesisVariants.length > 1) {
        console.log('🔧 [Global Self-Eval] Multiple synthesis variants - keeping all', {
          variants: synthesisVariants.map(v => v.modelId)
        });
      }

      finalData.sort((a, b) => {
        const mjComparison = compareMajorityJudgments(a.majorityJudgment, b.majorityJudgment);
        if (mjComparison !== 0) return mjComparison;
        // Final tie-breaker: alphabetical by modelId (deterministic)
        return a.modelId.localeCompare(b.modelId);
      });

      setData(finalData);
    });

    unsubscribeRef.current = unsubscribe;

    return () => {
      isMounted = false;
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [user, prioritySetting]);

  return data;
}
